import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react'

import { useEditor } from '@toasttab/sites-components'
import classnames from 'classnames'
import isEqual from 'lodash/isEqual'
import { useParams, useHistory, useLocation } from 'react-router'

import {
  EcommercePage,
  HeroContentType,
  MenuTemplate,
  StyleIntensity,
  MenuFormatConfig
} from 'src/apollo/sites'
import { t } from 'src/translations'

import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import { useRestaurantRoutes } from 'shared/components/common/restaurant_routes/RestaurantRoutesContext'

import { EcommStickyFooter } from 'public/components/default_template/EcommStickyFooter'
import MenuPageHeader from 'public/components/default_template/menu_header/MenuPageHeader'
import { getTemplateClass } from 'public/components/default_template/menu_section/MenuItemCard'
import { ItemResolutionModal } from 'public/components/default_template/online_ordering/item_modal/ItemResolutionModal'
import { MenusResponse } from 'public/components/online_ordering/useOOMenu'
import { useEcommMenus } from 'public/components/online_ordering/useEcommMenus'
import { useOOLocations } from 'public/components/online_ordering/FulfillmentContext'
import { EcommPageContexts } from 'src/public/components/default_template/online_ordering/pages/EcommPageContexts'

import { TrackingOrderPage } from './OrderPage'
import { MenuBanner } from './menu_banner/MenuBanner'
import {
  useFilterParams,
  useUpdateFiltersURL,
  isValidFilter
} from './menu_banner/urlFilterUtils'
import { useCategoryFromSlug } from './menu_banner/useCategoryFromSlug'
import {
  EcommercePageLayout,
  EcommercePageLayoutChildProps
} from './online_ordering/pages/EcommercePageLayout'
import { FilterTags } from './paginated_menu/FilterTags'
import { MenuContent } from './paginated_menu/MenuContent'
import { FilterAndSortNav } from 'public/components/default_template/filter_and_sort/FilterAndSortNav'
import { useFilterCriteria } from 'public/components/default_template/filter_and_sort/FilterCriteriaContext'
import { ErrorNavigationModal } from './online_ordering/location_selection/ErrorNavigationModal'
import { ChangeLocationConfirmationModal } from './online_ordering/location_selection/ChangeLocationModal'
import { useLocationSorting } from 'shared/js/hooks/useLocationSorting'
import { useFlags } from 'shared/components/common/feature_flags/useFlags'
import { useMenuGroupings } from './MenuGroupingsContext'

type EcommPageContentProps = {
  ecommContent: EcommercePage
  categorySlug?: string
  scrollNavRef?: React.Ref<HTMLDivElement>
  urlFilters?: Set<string>
  updateFiltersURL?: (filters: Set<string>, replace?: boolean) => void
  hideHero?: boolean
} & EcommercePageLayoutChildProps

const SitesEcommPageContent = (props: EcommPageContentProps) => {
  const urlFilters = useFilterParams()
  const updateFiltersURL = useUpdateFiltersURL()

  const { selectedLocation } = useRestaurant()
  const { slug } = useParams<{ slug?: string }>()
  const history = useHistory()
  const location = useLocation()
  const { baseEcommPath, ecommPath } = useRestaurantRoutes()

  useEffect(() => {
    // If we're on the base ecomm route and the rx has multiple locations, append the short url to the path

    const currentPath = location.pathname

    if (
      !slug &&
      ecommPath &&
      ecommPath != baseEcommPath &&
      currentPath.endsWith(baseEcommPath || '')
    ) {
      history.replace(ecommPath)
    }
  }, [
    slug,
    baseEcommPath,
    ecommPath,
    selectedLocation?.shortUrl,
    history,
    location?.pathname
  ])

  return (
    <WrappedEcommPageContent
      {...props}
      urlFilters={urlFilters}
      updateFiltersURL={updateFiltersURL}
    />
  )
}

const EditorEcommPageContent = (props: EcommPageContentProps) => {
  return <WrappedEcommPageContent {...props} />
}

export const EcommPageContent = (props: EcommPageContentProps) => {
  const { isEditor } = useEditor()
  if (isEditor) {
    return <EditorEcommPageContent {...props} />
  }
  return <SitesEcommPageContent {...props} />
}

const WrappedEcommPageContent = ({
  ecommContent,
  categorySlug,
  scrollNavRef,
  hideHero,
  urlFilters = new Set(),
  updateFiltersURL = () => {}
}: EcommPageContentProps) => {
  const {
    restaurant: siteRestaurant,
    selectedLocation,
    ooRestaurant: restaurant,
    updateLocation,
    ecommSettings
  } = useRestaurant()
  const ooLocations = useOOLocations()
  const { sortedLocations, userLocation } = useLocationSorting(ooLocations, {
    autoRequestLocation: true
  })
  const navRef = useRef<HTMLDivElement>(null)

  // State for initial location prompt modal
  const [showInitialLocationModal, setShowInitialLocationModal] =
    useState(false)
  const [hasCheckedInitialPrompt] = useState(false)

  const INITIAL_LOCATION_PROMPT_KEY = 'ecomm_initial_location_prompted'

  const { isEditor, useEditableRef } = useEditor()
  const { editableRef } = useEditableRef<HTMLDivElement>({
    name: 'menu',
    displayName: 'Menu',
    actions: [],
    schema: { fields: [] },
    skipRef: navRef
  })
  const channelGuid = selectedLocation?.ecommMenuChannelGuid || undefined
  const { ooEcommFilterAndSort } = useFlags()

  const headerRefPath = useMemo(() => {
    const ecommPageIndex = siteRestaurant.additionalPages?.findIndex(
      (page) => page.pageType === 'ecommerce'
    )
    return `additionalPages[${ecommPageIndex}].content.config.hero.heroImage`
  }, [siteRestaurant.additionalPages])

  const { filterCriteria, inStockOnly, clearFilterCriteria } =
    useFilterCriteria()

  // Filters are scoped to a single category, so reset them when the active
  // category changes (the provider persists across ecomm route changes).
  useEffect(() => {
    clearFilterCriteria()
  }, [categorySlug, clearFilterCriteria])

  const hideOutOfStockItems =
    (ecommContent.config?.hideOutOfStockItems ?? false) || inStockOnly

  const {
    menuItems,
    products,
    groupings,
    availableFilters,
    filters,
    setFilters,
    sort,
    setSort,
    totalCount,
    fetchMore,
    loadingNewFilterData,
    loading,
    hasMore
  } = useEcommMenus({
    respectAvailability: !isEditor,
    channelGuid,
    requirePagination: true,
    categorySlug,
    consolidateItemsByProduct: true,
    includeAvailableFilters: ooEcommFilterAndSort ?? false,
    includeProductOptionFilters: ooEcommFilterAndSort ?? false,
    filterCriteria,
    hideOutOfStockItems
  })

  // Persist groupings across category navigation
  const { groupings: persistedGroupings, setGroupings } = useMenuGroupings()
  useEffect(() => {
    if (groupings && groupings.length > 0) {
      setGroupings(groupings)
    }
  }, [groupings, setGroupings])

  const displayGroupings = groupings || persistedGroupings || []

  // Check if we should show initial location prompt
  useEffect(() => {
    // Only run once and only on client-side
    if (hasCheckedInitialPrompt || typeof localStorage === 'undefined') {
      return
    }

    if (
      !isEditor &&
      ooLocations &&
      ooLocations.length > 1 &&
      userLocation &&
      sortedLocations.length > 0
    ) {
      const hasBeenPrompted =
        localStorage.getItem(INITIAL_LOCATION_PROMPT_KEY) === 'true'

      if (!hasBeenPrompted) {
        // Check if closest location is different from selected
        const closestLocation = sortedLocations[0]
        if (closestLocation?.externalId !== selectedLocation?.externalId) {
          setShowInitialLocationModal(true)
        } else {
          // Same location, mark as prompted
          localStorage.setItem(INITIAL_LOCATION_PROMPT_KEY, 'true')
        }
      }
    }
  }, [
    isEditor,
    ooLocations,
    userLocation,
    sortedLocations,
    selectedLocation,
    hasCheckedInitialPrompt
  ])

  // Handle initial location modal close/cancel
  const closeAndSetPrompted = useCallback(() => {
    setShowInitialLocationModal(false)
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(INITIAL_LOCATION_PROMPT_KEY, 'true')
    }
  }, [])

  // Handle confirming location change to closest location
  const confirmLocationChange = useCallback(() => {
    const closestLocation = sortedLocations[0]
    if (closestLocation?.externalId) {
      updateLocation(closestLocation.externalId)
    }
    closeAndSetPrompted()
  }, [sortedLocations, updateLocation, closeAndSetPrompted])

  // Local state to manage filters
  const [localFilters, setLocalFilters] = useState<Set<string>>(new Set())

  // Get category from slug using the hook
  const currentCategory = useCategoryFromSlug(categorySlug, groupings)
  const categoryName = currentCategory?.name || null
  const parentSlugs = currentCategory?.parentSlugs || null

  // URL-aware filter update function
  const setAllFiltersAndUpdateURL = useCallback(
    (newFilters: Set<string>) => {
      setLocalFilters(newFilters)
      setFilters?.(newFilters)
      updateFiltersURL(newFilters)
    },
    [setFilters, updateFiltersURL]
  )

  //TODO: turn off this if feature flag is enabled
  // Initialize filters from URL on mount and when URL changes
  useEffect(() => {
    if (groupings && urlFilters?.size > 0) {
      // Validate URL filters against menu structure
      const validFilters = new Set<string>()
      for (const filter of urlFilters) {
        if (isValidFilter(filter, groupings)) {
          validFilters.add(filter)
        }
      }

      // Only update if there are valid filters and they differ from current
      if (!isEqual(localFilters, validFilters)) {
        setLocalFilters(validFilters)
        setFilters?.(validFilters)

        // Clean up URL if some filters were invalid
        if (validFilters.size !== urlFilters.size) {
          updateFiltersURL(validFilters, true) // replace to clean invalid filters
        }
      }
    } else if (urlFilters.size === 0 && localFilters.size > 0) {
      // URL has no filters, clear local filters
      setLocalFilters(new Set())
      setFilters?.(new Set())
    }
  }, [urlFilters, groupings, localFilters, setFilters, updateFiltersURL])

  // Ignore menuItems whenever products are present.
  // useOOMenus backfills menuItems with all product variants for callers that don't consume products.
  // EcommPage is the only caller that reads products, so passing both would render each item twice — once per array.
  // In the editor, also cap at 50 items for faster preview performance.
  let usedMenuItems: NonNullable<MenusResponse['items']> | undefined = undefined
  if (!products?.length) {
    usedMenuItems = isEditor ? menuItems?.slice(0, 50) : menuItems
  }
  const usedProducts = isEditor ? products?.slice(0, 50) : products ?? undefined

  // hideOutOfStockItems is ignored for paginated menus, so subtract the OOS items found in already-loaded pages
  const filteredTotalCount = useMemo(() => {
    if (!hideOutOfStockItems || totalCount == null) return totalCount
    const oosInLoadedItems =
      usedMenuItems?.filter((item) => item.outOfStock).length ?? 0
    const oosInLoadedProducts =
      usedProducts?.filter((product) => product.outOfStock).length ?? 0
    return Math.max(0, totalCount - oosInLoadedItems - oosInLoadedProducts)
  }, [hideOutOfStockItems, totalCount, usedMenuItems, usedProducts])

  const formatConfig = useMemo(() => {
    const format = ecommContent.config?.format
    return {
      ...format,
      template: format?.template
        ? (format.template as MenuTemplate)
        : MenuTemplate.TopImageV2,
      borderStroke: format?.borderStroke ?? StyleIntensity.Subtle,
      roundedCorner: format?.roundedCorner ?? StyleIntensity.Subtle,
      mobileOptimization: format?.mobileOptimization ?? true,
      condenseImagelessGroups: true
    } as MenuFormatConfig
  }, [ecommContent.config?.format])

  return (
    <>
      {(!menuItems || menuItems.length === 0) &&
      (!products || products.length === 0) &&
      !loading &&
      !filterCriteria &&
      ooLocations?.length &&
      selectedLocation?.externalId !== ooLocations[0]?.externalId ? (
        <ErrorNavigationModal isOpen={true} onClose={() => {}} />
      ) : null}
      <MenuBanner
        menuGroups={displayGroupings}
        setFilters={setAllFiltersAndUpdateURL}
        categorySlug={categorySlug}
        channelGuid={channelGuid}
        isEcomm
        loading={loading && !persistedGroupings}
      />
      {!hideHero && (
        <MenuPageHeader
          heading={
            categorySlug
              ? categoryName ?? ''
              : ecommContent.headingText ?? t('ecomm-page.online-store')
          }
          subHeading={categorySlug ? undefined : ecommContent.subheadingText}
          heroConfig={ecommContent.config?.hero as HeroContentType}
          refPath={headerRefPath}
          additionalHeaders={null}
          displayOptions={{
            showDiningOptionBehaviorMessage:
              !categorySlug && !ecommContent.config?.hideLocationDetails,
            showHeroImage: !categorySlug
          }}
          breadcrumbs={
            categoryName
              ? { name: categoryName, parentSlugs: parentSlugs }
              : null
          }
          loading={!!categorySlug && loading}
        />
      )}
      <div
        className='pageContent'
        ref={scrollNavRef}
        data-testid='order-page'
        role='main'
        id='main'
        aria-label={t('ecomm-page.menu')}
      >
        {!restaurant?.hasOnlineOrderingModule ? (
          <div className='onlineOrderingUnavailable'>
            {t('ecomm-page.ordering-unavailable')}
          </div>
        ) : (
          <div ref={editableRef}>
            <div className='navAndMenu'>
              {ooEcommFilterAndSort && (
                <FilterAndSortNav
                  onSortChange={setSort}
                  currentSort={sort}
                  totalCount={filteredTotalCount}
                  loading={loading}
                  loadingNewFilterData={loadingNewFilterData}
                  availableFilters={availableFilters}
                  filterSettings={ecommSettings?.filterSettings}
                  mobileOptimized={formatConfig.mobileOptimization ?? false}
                  hideOutOfStockItems={
                    ecommContent.config?.hideOutOfStockItems ?? false
                  }
                />
              )}
              <div className='menuSections'>
                {(usedMenuItems?.length || usedProducts?.length) &&
                groupings &&
                !categorySlug ? (
                  <>
                    <FilterTags
                      groupings={groupings}
                      filters={filters}
                      setFilters={setAllFiltersAndUpdateURL}
                    />
                  </>
                ) : null}
                <div
                  className={classnames(
                    'menuSectionWrapper',
                    getTemplateClass(formatConfig.template)
                  )}
                >
                  <MenuContent
                    loading={loading ?? false}
                    usedMenuItems={usedMenuItems}
                    products={usedProducts}
                    channelGuid={channelGuid}
                    fetchMore={fetchMore}
                    hasMore={hasMore}
                    loadingNewFilterData={loadingNewFilterData ?? false}
                    formatConfig={formatConfig}
                    ecommColors={ecommContent.config?.colors}
                    hideOutOfStockItems={hideOutOfStockItems}
                  />
                </div>
              </div>
            </div>
            <EcommStickyFooter showFooter />
          </div>
        )}
      </div>
      {/*TODO: Create EcommPageSchema */}
      {/*<OrderPageSchema restaurant={restaurant} site={siteRestaurant} selectedLocation={selectedLocation} locationSchedule={locationSchedule} menus={menus} />*/}
      {/* Only necessary when the guest has attempted to add an item to the cart that conflicts with the cart's current selections */}
      <ItemResolutionModal channelGuid={channelGuid} />
      {/* Initial location prompt modal */}
      {sortedLocations[0] && (
        <ChangeLocationConfirmationModal
          isOpen={showInitialLocationModal}
          onClose={closeAndSetPrompted}
          onConfirm={confirmLocationChange}
          onCancel={closeAndSetPrompted}
          selectedLocation={sortedLocations[0] as any}
          headerText={t('confirm-location-modal.closest-location')}
        />
      )}
    </>
  )
}

const EcommPage = ({ ecommContent }: { ecommContent: EcommercePage }) => {
  const { isEditor } = useEditor()

  // In production, EcommPageContexts is satisfied by EcommerceProvidersWrapper at the routing level.
  // In the editor, there is no routing wrapper, so we provide EcommPageContexts directly here.
  if (isEditor) {
    return (
      <EcommPageContexts>
        <EcommercePageLayout>
          {(props) => (
            <TrackingOrderPage>
              <EcommPageContent ecommContent={ecommContent} {...props} />
            </TrackingOrderPage>
          )}
        </EcommercePageLayout>
      </EcommPageContexts>
    )
  }

  return (
    <EcommercePageLayout>
      {(props) => (
        <TrackingOrderPage>
          <EcommPageContent ecommContent={ecommContent} {...props} />
        </TrackingOrderPage>
      )}
    </EcommercePageLayout>
  )
}
export default React.memo(EcommPage)
